import { alpha, Box, Paper, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Button } from '@uplift/ui-v2';

const useStyles = makeStyles()(theme => ({
  root: {
    maxWidth: 700,
    width: '100%',
    margin: '0 auto',
    padding: '80px',
  },
  overline: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    letterSpacing: 1,
  },
  title: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  subtitle: {
    marginBottom: theme.spacing(4),
  },
  options: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  card: {
    padding: theme.spacing(2),
    cursor: 'pointer',
    borderWidth: 2,
    borderColor: theme.palette.divider,
    borderRadius: '8px',
  },
  cardSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(2),
  },
}));

export type QuestionOption = {
  value: string;
  title: string;
  description: string;
};

export type QuestionStepProps = {
  overline?: string;
  title: string;
  subtitle: string;
  options: QuestionOption[];
  value: string | null;
  onChange: (value: string) => void;
  onContinue: () => void;
  onBack: () => void;
};

export const QuestionStep = ({
  overline,
  title,
  subtitle,
  options,
  value,
  onChange,
  onContinue,
  onBack,
}: QuestionStepProps) => {
  const { classes, cx } = useStyles();

  return (
    <Box className={classes.root}>
      {overline && (
        <Typography variant="overline" className={classes.overline}>
          {overline}
        </Typography>
      )}

      <Typography variant="h4" fontWeight={700} sx={{ fontSize: '30px' }} className={classes.title}>
        {title}
      </Typography>

      <Typography color="text.secondary" className={classes.subtitle}>
        {subtitle}
      </Typography>

      <Box className={classes.options}>
        {options.map(option => {
          const selected = value === option.value;

          return (
            <Paper
              key={option.value}
              variant="outlined"
              onClick={() => onChange(option.value)}
              className={cx(classes.card, { [classes.cardSelected]: selected })}
            >
              <Typography>{option.title}</Typography>
              <Typography color="text.secondary">{option.description}</Typography>
            </Paper>
          );
        })}
      </Box>

      <Box className={classes.actions}>
        <Button variant="contained" color="primary" disabled={!value} onClick={onContinue}>
          Continue
        </Button>
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
      </Box>
    </Box>
  );
};
